import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';
import { LoadingService } from '../../../../../shared/services/loading.service';

import { ImportAgeingService } from '../../../../../shared/services/import-ageing-service';
import { DialogService } from '../../../../../shared/controls/message-dialog/message-dialog.service';
import { AgeingBatchResult, AgeingService, AgeingTotalsResult, SampleAgeingDataResult } from '../../../../../../service/proxy';
import { buildFilteredErrors, formatErrorValue } from '../../../../../shared/helpers/import-error-helper';


@Component({
    selector: 'ageing-import-data',
    templateUrl: './ageing-import-data.component.html',
    styleUrls: ['./ageing-import-data.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        AgeingService,
    ]
})
export class AgeingImportDataComponent implements OnInit, OnDestroy, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;

    public batch: AgeingBatchResult = new AgeingBatchResult();
    public totals: AgeingTotalsResult = new AgeingTotalsResult();
    public sampleData: SampleAgeingDataResult = new SampleAgeingDataResult();

    public importInProgress: boolean = false;
    public importProgress: number = 0;
    public importCompleted: boolean = false;
    public importErrors: any[] = []
    public showErrors = false;
    public totalErrorCount: number = 0;
    public errorRowCount: number = 0;
    public totalRows: number = 0;
    public rowsImported: number = 0;

    public loading: boolean = false;
    public loadingTotals: boolean = false;

    public filteredErrors: any[] = [];
    public showData = false;
    public headers: string[] = [];

    constructor(
        private route: ActivatedRoute,
        private ageingService: AgeingService,
        private importAgeingService: ImportAgeingService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private dialogService: DialogService,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    ngOnDestroy(): void {
        if (this.importAgeingService) {
            this.importAgeingService.stopListening();
        }
    }

    processErrors(): void {
        this.filteredErrors = buildFilteredErrors(this.importErrors, 10);
    }

    formatErrorValue(value: any): string {
        return formatErrorValue(value, 120);
    }

    onActivate(): void {
        this.route.paramMap
            .subscribe(params => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    const mappingsChangedKey = `ageing_mappings_changed_${this.batchId}`;
                    const mappingsChanged = sessionStorage.getItem(mappingsChangedKey) === 'true';

                    if (mappingsChanged) {
                        sessionStorage.removeItem(mappingsChangedKey);
                        this.deleteExistingDataAndImport();
                        return;
                    }

                    this.loadExistingBatchData();
                }
            }
        );
    }

    loadExistingBatchData(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.ageingService.calculateAgeingTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.totals = response;
                    this.loadingTotals = false;
                    this.loadingService.hide();

                    const alreadyImported = (this.totals?.totalRowsImported || 0) > 0;

                    if (alreadyImported) {
                        this.importCompleted = true;
                        this.importProgress = 100;
                        this.importInProgress = false;
                        this.cdr.detectChanges();

                        return;
                    }

                    this.deleteExistingDataAndImport();
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.toastService.show('Failed to load batch data. Please try again.', 'Error');
                    this.errorService.handleError(error);
                }
            });
    }

    deleteExistingDataAndImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.importProgress = 0;
        this.importErrors = [];
        this.filteredErrors = [];
        this.showErrors = false;
        this.showData = false;
        this.cdr.detectChanges();

        this.ageingService.deleteAgeingDataByBatchId(this.batchId)
            .subscribe({
                next: () => {
                    this.startImport();
                },
                error: (error) => {
                    this.importInProgress = false;
                    this.cdr.detectChanges();
                    this.errorService.handleError(error);
                }
            });
    }

    startImport(): void {
        this.importInProgress = true;
        this.importCompleted = false;
        this.cdr.detectChanges();

        this.importAgeingService.startListening(this.batchId, (entry) => {
                if (entry) {
                    this.importProgress = entry.progress;
                    this.importErrors = entry.errors || [];
                    this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                    this.errorRowCount = entry.errorRowCount || 0;
                    this.totalRows = entry.totalRows || 0;
                    this.rowsImported = entry.rowsImported || 0;
                    this.cdr.detectChanges();
                }
                else {
                    this.toastService.show('No progress data received.', 'Info');
                }

                if (this.importProgress === 100 || entry.message === 'Import completed.') {
                    this.importInProgress = false;
                    this.importErrors = entry.errors || [];
                    this.totalErrorCount = entry.totalErrorCount || entry.errors?.length || 0;
                    this.errorRowCount = entry.errorRowCount || 0;
                    this.totalRows = entry.totalRows || 0;
                    this.rowsImported = entry.rowsImported || 0;
                    this.showErrors = this.importErrors.length > 0;
                    this.processErrors();
                    this.cdr.detectChanges();

                    setTimeout(() => {
                        this.importInProgress = false;
                        this.importCompleted = true;

                        this.cdr.detectChanges();
                        this.getTotals();
                        this.showData = false;
                    }, 3000);
                }
            },
            () => {
                this.importInProgress = false;

                this.cdr.detectChanges();
                this.toastService.show('Failed to start ageing data import.', 'Error');
            });
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            observer.next(true);
            observer.complete();
        });
    }

    onPrevious(): Observable<boolean> {
        // Reset completion flag so returning to this step will re-import
        this.importCompleted = false;
        return scheduled([true], asyncScheduler);
    }

    isValid(): boolean {
         return this.importErrors.length === 0;
    }

    getTotals(): void {
        this.loadingTotals = true;
        this.loadingService.show();
        this.cdr.detectChanges();

        this.ageingService.calculateAgeingTotals(this.batchId)
            .subscribe({
                next: (response) => {
                    this.totals = response;
                    this.loadingTotals = false;
                    this.loadingService.hide();
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    this.loadingTotals = false;
                    this.loadingService.hide();

                    if (error.status === 400) {
                        this.toastService.show('Failed to fetch import batch data. Please try again.', 'Error');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('Not found. Please try again.', 'Info');
                    }
                    else {
                        this.toastService.show('Failed to load batch data. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    toggleSampleData(): void {
        if (!this.showData) {
           if (!this.sampleData ||
                !Array.isArray(this.sampleData.sampleRows) ||
                this.sampleData.sampleRows.length === 0)
            {
                this.getSampleData();
            }
            else {
                this.showData = true;
                this.cdr.detectChanges();
            }
        }
        else {
            this.showData = false;
            this.cdr.detectChanges();
        }
    }

    getSampleData(): void {
        this.loading = true;
        this.ageingService.getAgeingSampleData(this.batchId)
            .subscribe({
                next: (response) => {
                    this.sampleData = response;

                    if (response.sampleRows && response.sampleRows.length > 0) {
                        this.headers = Object.keys(response.sampleRows[0]);
                    }
                    this.showData = true;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'Info');
                    }
                    else if (error.status === 404) {
                        this.toastService.show('No sample data found for this batch.', 'Info');
                    }
                    else {
                        this.toastService.show('Failed to fetch sample batch data. Please try again.', 'Error');
                        this.errorService.handleError(error);
                    }
                },
                complete: () => {
                    this.loading = false;
                    this.cdr.detectChanges();
                }
            });
    }

    formatNumber(value: number): string {
        if (!value) {
            return '0';
        }

        return value.toLocaleString('en-US');
    }

    formatHeader(header: string): string {
        let formatted = header.replace(/_/g, ' ');

        formatted = formatted.replace(/([A-Z])/g, ' $1');
        formatted = formatted.trim();
        formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);

        return formatted;
    }

    toggleError() {
        this.showErrors = !this.showErrors;
    }

    deleteAgeingBatch(): void {
        if (!this.batchId) {
            this.toastService.show('No batch selected to delete.', 'Error');
            return;
        }
        this.dialogService.show('Delete Pending Batch', 'Are you sure you want to delete this batch?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.ageingService.deletePendingAgeingBatch(this.batchId)
                        .subscribe({
                            next: () => {
                                this.toastService.show('Ageing batch deleted successfully.', 'success');
                                this.router.navigate(['/ageing-create']);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            }
                        }
                    );
                }
            }
        );
    }
}